import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api, { apiRoutes } from '../api';
import { fetchNextDrawIfAvailable, prefetchedSiteDetails, prefetchedNextDraw } from '../api/prefetch';
import { getConfig } from '../config/environment';
import { useColorTheme } from './ColorThemeContext';
import { useCountdownStyle } from './CountdownStyleContext';
import { useGameType } from './GameTypeContext';
import { useLanguage } from './LanguageContext';
import { useAuthConfig } from './AuthConfigContext';
import { useLayoutStyle } from './LayoutStyleContext';
import { mapNextDrawForFrontend, unwrapOperationData } from '../utils/drawsApi';
import { isDrawClosed } from '../utils/drawLifecycle';

const SiteConfigContext = createContext(undefined);
export { SiteConfigContext };

export function SiteConfigProvider({ children }) {
  const { tlfId } = getConfig();

  const [siteDetails, setSiteDetails] = useState(() => {
    try {
      const cached = localStorage.getItem('site_details');
      if (cached) {
        const parsed = JSON.parse(cached);
        const cachedOrgId = parsed.organizationId || parsed.orgId || parsed.TLF_ID || parsed.organization_id;
        if (!cachedOrgId || !tlfId || cachedOrgId === tlfId) {
          return parsed;
        }
      }
    } catch {}
    return null;
  });
  const [plans, setPlans] = useState([]);
  const [nextDraw, setNextDraw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { changeColorTheme } = useColorTheme();
  const { updateCountdownStyle } = useCountdownStyle();
  const { changeGameType } = useGameType();
  const { toggleLanguage, updateEnabledLanguages } = useLanguage();
  const { populateFromSiteDetails } = useAuthConfig();
  const { changeLayoutStyle } = useLayoutStyle();

  // Only use the module-level prefetched promises on the very first load.
  // On subsequent refetches (e.g. after a purchase) we must hit the API again,
  // otherwise we'd re-await the stale, already-resolved prefetch promises.
  const prefetchConsumed = useRef(false);

  const fetchSiteDetails = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Use module-level prefetched promises (fired at import time, before React mounted)
      // to eliminate sequential wait. Falls back to fresh fetch if prefetch is null
      // or has already been consumed by a previous load.
      const usePrefetch = !prefetchConsumed.current;
      prefetchConsumed.current = true;
      const currentDomain = typeof window !== 'undefined' && window.location ? window.location.hostname : '';
      const [detailsRes, nextDrawRes] = await Promise.allSettled([
        (usePrefetch && prefetchedSiteDetails) || api.get(apiRoutes.SiteDetails, { params: { organizationId: tlfId, domain: currentDomain } }),
        (usePrefetch && prefetchedNextDraw) || fetchNextDrawIfAvailable(tlfId),
      ]);

      // Site details
      if (detailsRes.status === 'fulfilled') {
        const payload = detailsRes.value.data?.payload || detailsRes.value.data;
        const details = payload?.details || payload;
        if (details) {
          setSiteDetails(details);
          try { localStorage.setItem('site_details', JSON.stringify(details)); } catch {}

          if (details.isWhiteLabel != null) {
            try { localStorage.setItem('site_is_white_label', String(details.isWhiteLabel)); } catch {}
          }
          if (details.whiteLabelImage) {
            try { localStorage.setItem('site_white_label_image', details.whiteLabelImage); } catch {}
          }

          // Push config to contexts
          if (details.colorTheme) {
            changeColorTheme(details.colorTheme);
            try { localStorage.setItem('site_color_theme', details.colorTheme); } catch {}
          }
          if (details.countdownStyle) updateCountdownStyle(details.countdownStyle);
          if (details.gameType) changeGameType(details.gameType);
          if (details.layoutStyle) changeLayoutStyle(details.layoutStyle);
          if (details.defaultDarkMode != null) {
            // Only apply the org's default theme when the visitor hasn't already
            // chosen one — otherwise we'd clobber their manual toggle on every load.
            let hasStoredPreference = false;
            try { hasStoredPreference = localStorage.getItem('darkMode') !== null; } catch {}
            if (!window.SITE_CONFIG) window.SITE_CONFIG = {};
            if (!window.SITE_CONFIG.defaults) window.SITE_CONFIG.defaults = {};
            window.SITE_CONFIG.defaults.default_dark_mode = !!details.defaultDarkMode;
            if (!hasStoredPreference) {
              window.dispatchEvent(new CustomEvent('orgDarkModeDefault', { detail: !!details.defaultDarkMode }));
            }
          }

          // Apply custom theme colors to SITE_CONFIG and CSS vars
          if (details.bgLight || details.bgDark || details.navBg || details.navText) {
            if (!window.SITE_CONFIG) window.SITE_CONFIG = {};
            if (!window.SITE_CONFIG.defaults) window.SITE_CONFIG.defaults = {};
            
            const root = document.documentElement;
            if (details.bgLight) {
              window.SITE_CONFIG.defaults.bg_light = details.bgLight;
              root.style.setProperty('--app-bg-light', details.bgLight);
              try { localStorage.setItem('site_bg_light', details.bgLight); } catch {}
            }
            if (details.bgDark) {
              window.SITE_CONFIG.defaults.bg_dark = details.bgDark;
              root.style.setProperty('--app-bg-dark', details.bgDark);
              try { localStorage.setItem('site_bg_dark', details.bgDark); } catch {}
            }
            if (details.navBg) {
              window.SITE_CONFIG.defaults.nav_bg = details.navBg;
              root.style.setProperty('--nav-bg', details.navBg);
              try { localStorage.setItem('site_nav_bg', details.navBg); } catch {}
            }
            if (details.navText) {
              window.SITE_CONFIG.defaults.nav_text = details.navText;
              root.style.setProperty('--nav-text', details.navText);
              try { localStorage.setItem('site_nav_text', details.navText); } catch {}
            }
          }

          if (Object.prototype.hasOwnProperty.call(details, 'defaultPageVisibility') || Object.prototype.hasOwnProperty.call(details, 'default_page_visibility')) {
            const defaultPageVisibility = details.defaultPageVisibility ?? details.default_page_visibility;
            if (!window.SITE_CONFIG) window.SITE_CONFIG = {};
            if (!window.SITE_CONFIG.defaults) window.SITE_CONFIG.defaults = {};
            window.SITE_CONFIG.defaults.default_page_visibility = defaultPageVisibility;
            try { localStorage.setItem('site_default_page_visibility', JSON.stringify(defaultPageVisibility)); } catch {}
          }

          if (details.primaryLanguage) {
            const langMap = { English: 'en', French: 'fr', Spanish: 'es', German: 'de', Italian: 'it', Portuguese: 'pt' };
            const langCode = langMap[details.primaryLanguage] || details.primaryLanguage?.toLowerCase()?.slice(0, 2) || 'en';
            // Only set language on first load if not already stored
            if (!localStorage.getItem('language')) {
              toggleLanguage(langCode);
            }
          }
          if (details.supportedLanguages) {
            const langMap = { English: 'en', French: 'fr', Spanish: 'es', German: 'de', Italian: 'it', Portuguese: 'pt' };
            const enabledLangs = details.supportedLanguages.split(',').map(l => langMap[l.trim()] || l.trim().toLowerCase().slice(0, 2));
            updateEnabledLanguages(enabledLangs);
          }

          // Push auth config
          populateFromSiteDetails(details);
        }
      }

      // Next draw
      let hasActiveDraw = false;
      if (nextDrawRes.status === 'fulfilled') {
        const rawDrawData = unwrapOperationData(nextDrawRes.value?.data);
        const drawData = mapNextDrawForFrontend(rawDrawData);
        if (drawData) {
          setNextDraw(drawData);
          // Only fetch plans if the draw is still open for purchases
          hasActiveDraw = !isDrawClosed({ nextDraw: drawData, siteDetails });
        }
      }

      // Mark loading complete — homepage can render with siteDetails + nextDraw.
      // Plans are fetched in the background (only needed for purchase page).
      setLoading(false);

      // Plans — only fetch if a purchasable draw exists (API returns 400 otherwise)
      if (hasActiveDraw) {
        try {
          const plansResponse = await api.get(apiRoutes.OrganizationPurchasePlans, { params: { organizationId: tlfId } });
          const raw = plansResponse.data?.payload;
          const plansData = Array.isArray(raw?.data) ? raw.data
            : Array.isArray(raw) ? raw
            : Array.isArray(plansResponse.data) ? plansResponse.data
            : [];
          if (plansData.length > 0) {
            setPlans(plansData);
          }
        } catch {
          // No plans available for current draw — not critical
        }
      }
    } catch (err) {
      console.error('Failed to load site config:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [tlfId]);

  useEffect(() => {
    fetchSiteDetails();
  }, [fetchSiteDetails]);

  const drawClosed = !loading && isDrawClosed({ nextDraw, siteDetails });

  const contextValue = {
    siteDetails,
    plans,
    nextDraw,
    setNextDraw, // Exposed for SignalR optimistic updates
    loading,
    error,
    isDrawClosed: drawClosed,
    refetch: fetchSiteDetails,
  };

  return (
    <SiteConfigContext.Provider value={contextValue}>
      {children}
    </SiteConfigContext.Provider>
  );
}

// Safe default so components don't crash if rendered outside the provider
// (e.g. during HMR, error recovery, or render-before-mount edge cases).
const SAFE_DEFAULT = {
  siteDetails: null,
  plans: [],
  nextDraw: null,
  setNextDraw: () => {},
  loading: true,
  error: null,
  isDrawClosed: false,
  refetch: () => {},
};

export function useSiteConfig() {
  const context = useContext(SiteConfigContext);
  if (context === undefined) {
    console.warn('useSiteConfig called outside SiteConfigProvider — returning safe defaults');
    return SAFE_DEFAULT;
  }
  return context;
}
